import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Button,
  Checkbox,
  Dropdown,
  Icon,
  Input,
  Modal,
} from 'semantic-ui-react';

import { useToasts } from 'common/toast';
import { PageContent, PageHeader, PageSection } from 'common/styles/page';
import { StyledButton } from 'common/components/button/StyledButton';
import userContextSlice from 'common/slices/userContextSlice';
import { getCurrentOrganization } from 'common/selectors/auth';

import { employeeApi } from '../api/employeeApi';
import MemberCreateModal from './MemberCreateModal';
import { END_OF_SERVICE_REASONS } from '../utils/endOfServiceReasons';


type ReviewTab = 'needs-review' | 'verified' | 'archived';

type TeamMembership = {
  team_id: number;
  team_name: string;
  team_external_id: string | null;
};

type MemberRow = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  departure_date?: string | null;
  departure_reason?: string | null;
  team_memberships?: TeamMembership[];
  status: number;
  demographic_values?: Record<string, string>;
  missing_demographic_fields?: string[];
  verified?: boolean;
  verified_at?: string | null;
  verified_by?: string | null;
};

type EditingCell = {
  memberId: number;
  field: string;
  value: any;
};

type NameCellIconName = 'archive' | 'check circle' | 'circle outline' | 'undo' | 'undo alternate';

type NameCellIndicator = {
  icon: NameCellIconName;
  label: string;
  color: string;
};

const COLUMN_PREFS_KEY = 'roster-review-visible-columns';
const COLUMN_PREFS_MIGRATION_KEY = 'roster-review-email-default-off-migrated-v1';
const ACTIVE_STATUS = 1;
const ALL_FIELDS_OPTION = '__all_fields__';
const ALL_VALUES_OPTION = '__all_values__';
const TEAM_MEMBERSHIP_COLUMN = 'team_membership';
const CHECKBOX_COL_WIDTH = 56;
const NAME_COL_WIDTH = 190;
const EMAIL_COL_WIDTH = 210;
const ACTION_COL_WIDTH = 78;
const FLEX_COL_MIN_WIDTH = 150;
const HORIZONTAL_SCROLL_STEP = 260;
const SCROLL_EDGE_EPSILON = 2;

const RosterReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToasts();

  const organization = useSelector(getCurrentOrganization);
  const demographicFields = useSelector(userContextSlice.selectors.getDemographicFields);

  const [activeTab, setActiveTab] = useState<ReviewTab>('needs-review');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [recentlySavedCells, setRecentlySavedCells] = useState<Set<string>>(new Set());
  const [verifyingIds, setVerifyingIds] = useState<Set<number>>(new Set());
  const [hoveredActionMemberId, setHoveredActionMemberId] = useState<number | null>(null);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createError, setCreateError] = useState<string | undefined>(undefined);
  const [isCreatingMember, setIsCreatingMember] = useState(false);
  const [isSelectionSummaryHovered, setIsSelectionSummaryHovered] = useState(false);
  const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false);
  const [isFinalizingReview, setIsFinalizingReview] = useState(false);

  const [listFilterField, setListFilterField] = useState<string>('');
  const [listFilterValue, setListFilterValue] = useState<string>('');
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'last_name',
    direction: 'asc',
  });

  const [visibleColumns, setVisibleColumns] = useState<string[]>(['name', TEAM_MEMBERSHIP_COLUMN]);
  const tableViewportRef = useRef<HTMLDivElement | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

  const orgId = organization?.id;
  const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const {
    data: rosterData,
    isLoading,
    error,
  } = employeeApi.useGetRosterReviewQuery(
    { organizationId: orgId || 0 },
    {
      skip: !orgId,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: rosterTeamsData,
  } = employeeApi.useGetRosterReviewTeamsQuery(
    { organizationId: orgId || 0 },
    {
      skip: !orgId,
      refetchOnMountOrArgChange: true,
    }
  );

  const [verifyRosterMembers, { isLoading: isVerifying }] = employeeApi.useVerifyRosterMembersMutation();
  const [unverifyRosterMembers, { isLoading: isUnverifying }] = employeeApi.useUnverifyRosterMembersMutation();
  const [archiveRosterMembers, { isLoading: isArchiving }] = employeeApi.useArchiveRosterMembersMutation();
  const [restoreRosterMembers, { isLoading: isRestoring }] = employeeApi.useRestoreRosterMembersMutation();
  const [completeRosterReview, { isLoading: isCompleting }] = employeeApi.useCompleteRosterReviewMutation();
  const [updateRosterMemberTeams, { isLoading: isUpdatingTeams }] = employeeApi.useUpdateRosterMemberTeamsMutation();
  const [createMember] = employeeApi.useApiPortalV1EmployeeEmployeesCreateMutation();
  const [updateMember] = employeeApi.useApiPortalV1EmployeeEmployeesPartialUpdateMutation();

  useEffect(() => {
    const raw = localStorage.getItem(COLUMN_PREFS_KEY);
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const hasMigratedEmailDefault = localStorage.getItem(COLUMN_PREFS_MIGRATION_KEY) === 'true';

        if (!hasMigratedEmailDefault) {
          const migrated = parsed.filter((column: string) => column !== 'email');
          setVisibleColumns(migrated.length > 0 ? migrated : ['name', TEAM_MEMBERSHIP_COLUMN]);
          localStorage.setItem(COLUMN_PREFS_MIGRATION_KEY, 'true');
          return;
        }

        setVisibleColumns(parsed);
      }
    } catch {
      // Ignore malformed persisted values.
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const demographicFieldEntries = useMemo(() => {
    if (!demographicFields) {
      return [] as Array<{ name: string; displayName: string; validValues?: Array<{ key: string; text: string }> }>;
    }

    return Object.values(demographicFields)
      .filter((field: any) => field && field.name)
      .map((field: any) => ({
        name: field.name,
        displayName: field.displayName || field.name,
        validValues: field.validValues || [],
      }));
  }, [demographicFields]);

  useEffect(() => {
    if (demographicFieldEntries.length === 0) {
      return;
    }

    setVisibleColumns(prev => {
      const identity = ['name', TEAM_MEMBERSHIP_COLUMN];
      const demographics = demographicFieldEntries
        .map(field => field.name)
        .filter(name => !prev.includes(name));
      const next = [...new Set([...identity, ...prev, ...demographics])];
      if (next.length === prev.length && next.every((column, index) => column === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [demographicFieldEntries]);

  const members = (rosterData?.members || []) as MemberRow[];
  const counts = rosterData?.counts || {
    active: 0,
    needs_review: 0,
    verified: 0,
    archived: 0,
  };
  const isRosterFinalized = Boolean(
    rosterData?.cycle?.completed_at
    && !rosterData?.cycle?.completion_invalidated_at
  );

  const filteredMembers = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return members.filter(member => {
      if (activeTab === 'needs-review' && !(member.status === ACTIVE_STATUS && !member.verified)) {
        return false;
      }
      if (activeTab === 'verified' && !(member.status === ACTIVE_STATUS && member.verified)) {
        return false;
      }
      if (activeTab === 'archived' && member.status === ACTIVE_STATUS) {
        return false;
      }

      if (!needle) {
        if (!listFilterField || !listFilterValue) {
          return true;
        }
        return (member.demographic_values?.[listFilterField] || '') === listFilterValue;
      }

      const searchMatches = (
        `${member.first_name || ''} ${member.last_name || ''}`.toLowerCase().includes(needle) ||
        `${member.last_name || ''}, ${member.first_name || ''}`.toLowerCase().includes(needle) ||
        (member.email || '').toLowerCase().includes(needle)
      );

      if (!searchMatches) {
        return false;
      }

      if (!listFilterField || !listFilterValue) {
        return true;
      }

      return (member.demographic_values?.[listFilterField] || '') === listFilterValue;
    });
  }, [members, activeTab, search, listFilterField, listFilterValue]);

  const sortedMembers = useMemo(() => {
    const getSortValue = (member: MemberRow, key: string): string | number => {
      if (key === 'last_name') {
        return `${member.last_name || ''}, ${member.first_name || ''}`.toLowerCase();
      }
      if (key === 'email') {
        return (member.email || '').toLowerCase();
      }
      if (key === TEAM_MEMBERSHIP_COLUMN) {
        return (member.team_memberships || [])
          .map(team => team.team_external_id || '')
          .join('|')
          .toLowerCase();
      }
      if (key === 'departure_date') {
        return member.departure_date || '';
      }
      if (key === 'departure_reason') {
        return (member.departure_reason || '').toLowerCase();
      }
      if (key === 'verified_at') {
        return member.verified_at || '';
      }

      return (member.demographic_values?.[key] || '').toLowerCase();
    };

    const sorted = [...filteredMembers].sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.key);
      const bVal = getSortValue(b, sortConfig.key);

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredMembers, sortConfig]);

  const handleSort = (key: string) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const filteredMemberIds = useMemo(() => sortedMembers.map(member => member.id), [sortedMembers]);
  const selectedVisibleIds = useMemo(
    () => filteredMemberIds.filter(id => selectedIds.has(id)),
    [filteredMemberIds, selectedIds]
  );
  const allVisibleSelected = filteredMemberIds.length > 0 && selectedVisibleIds.length === filteredMemberIds.length;

  const activeTotal = counts.active || 0;
  const verifiedCount = counts.verified || 0;
  const hasUsersNeedingReview = (counts.needs_review || 0) > 0;

  const inFlight = isVerifying || isUnverifying || isArchiving || isRestoring || isCompleting || isUpdatingTeams;
  const isArchivedTab = activeTab === 'archived';
  const isVerifiedTab = activeTab === 'verified';
  const demographicFieldNameSet = useMemo(
    () => new Set(demographicFieldEntries.map(field => field.name)),
    [demographicFieldEntries]
  );
  const dynamicColumns = isArchivedTab
    ? ['departure_date', 'departure_reason']
    : visibleColumns.filter(
      column => column !== 'name'
        && column !== 'email'
        && (column === TEAM_MEMBERSHIP_COLUMN || demographicFieldNameSet.has(column))
    );
  const hasEmailColumn = visibleColumns.includes('email');
  const flexibleColumnCount = dynamicColumns.length + (isVerifiedTab ? 1 : 0);

  const gridTemplateColumns = useMemo(() => {
    const template = [`${CHECKBOX_COL_WIDTH}px`, `${NAME_COL_WIDTH}px`];

    if (hasEmailColumn) {
      template.push(`${EMAIL_COL_WIDTH}px`);
    }

    for (let i = 0; i < flexibleColumnCount; i += 1) {
      template.push(`minmax(${FLEX_COL_MIN_WIDTH}px, 1fr)`);
    }

    template.push(`${ACTION_COL_WIDTH}px`);
    return template.join(' ');
  }, [flexibleColumnCount, hasEmailColumn]);

  const gridMinWidth = useMemo(
    () => CHECKBOX_COL_WIDTH
      + NAME_COL_WIDTH
      + (hasEmailColumn ? EMAIL_COL_WIDTH : 0)
      + ACTION_COL_WIDTH
      + (flexibleColumnCount * FLEX_COL_MIN_WIDTH),
    [flexibleColumnCount, hasEmailColumn]
  );

  const toggleSelection = (memberId: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };

  const scrollGridHorizontally = (direction: 'left' | 'right') => {
    const viewport = tableViewportRef.current;
    if (!viewport) {
      return;
    }

    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    if (maxScrollLeft <= SCROLL_EDGE_EPSILON) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const delta = direction === 'left' ? -HORIZONTAL_SCROLL_STEP : HORIZONTAL_SCROLL_STEP;
    const nextScrollLeft = Math.min(
      maxScrollLeft,
      Math.max(0, viewport.scrollLeft + delta)
    );

    if (Math.abs(nextScrollLeft - viewport.scrollLeft) <= SCROLL_EDGE_EPSILON) {
      setCanScrollLeft(nextScrollLeft > SCROLL_EDGE_EPSILON);
      setCanScrollRight(nextScrollLeft < maxScrollLeft - SCROLL_EDGE_EPSILON);
      return;
    }

    viewport.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
    setCanScrollLeft(nextScrollLeft > SCROLL_EDGE_EPSILON);
    setCanScrollRight(nextScrollLeft < maxScrollLeft - SCROLL_EDGE_EPSILON);
  };

  useEffect(() => {
    const viewport = tableViewportRef.current;
    if (!viewport) {
      return;
    }

    const updateScrollAffordances = () => {
      const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const nextCanScrollLeft = viewport.scrollLeft > SCROLL_EDGE_EPSILON;
      const nextCanScrollRight = viewport.scrollLeft < maxScrollLeft - SCROLL_EDGE_EPSILON;
      setCanScrollLeft(nextCanScrollLeft);
      setCanScrollRight(nextCanScrollRight);
    };

    updateScrollAffordances();

    viewport.addEventListener('scroll', updateScrollAffordances, { passive: true });
    window.addEventListener('resize', updateScrollAffordances);

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateScrollAffordances)
      : null;

    resizeObserver?.observe(viewport);

    return () => {
      viewport.removeEventListener('scroll', updateScrollAffordances);
      window.removeEventListener('resize', updateScrollAffordances);
      resizeObserver?.disconnect();
    };
  }, [gridTemplateColumns, sortedMembers.length]);

  const saveCell = async (member: MemberRow, field: string, value: string | number[]) => {
    if (!orgId) {
      return;
    }

    const normalized = typeof value === 'string' ? value.trim() : value;
    const payload: any = {};

    if (field === TEAM_MEMBERSHIP_COLUMN) {
      try {
        await updateRosterMemberTeams({
          organizationId: orgId,
          employeeId: member.id,
          teamIds: Array.isArray(value) ? value : [],
        }).unwrap();

        const cellId = `${member.id}:${field}`;
        setRecentlySavedCells(prev => new Set(prev).add(cellId));
        setTimeout(() => {
          setRecentlySavedCells(prev => {
            const next = new Set(prev);
            next.delete(cellId);
            return next;
          });
        }, 1300);

        addToast('Member teams saved', { appearance: 'success', autoDismiss: true });
      } catch {
        addToast('Failed to save member teams', { appearance: 'error', autoDismiss: true });
      }

      return;
    }

    if (field === 'first_name' || field === 'last_name' || field === 'email') {
      payload[field] = normalized;
    } else if (field === 'departure_date' || field === 'departure_reason') {
      payload[field] = normalized || null;
    } else {
      const fullDemographics = {
        ...(member.demographic_values || {}),
        [field]: normalized,
      };
      payload.demographic_data = fullDemographics;
    }

    try {
      await updateMember({
        id: member.id,
        pk: member.id,
        patchedPatchEmployee: payload,
      }).unwrap();

      const cellId = `${member.id}:${field}`;
      setRecentlySavedCells(prev => new Set(prev).add(cellId));
      setTimeout(() => {
        setRecentlySavedCells(prev => {
          const next = new Set(prev);
          next.delete(cellId);
          return next;
        });
      }, 1300);

      addToast('Member saved', { appearance: 'success', autoDismiss: true });
    } catch (saveError) {
      addToast('Failed to save member change', { appearance: 'error', autoDismiss: true });
    }
  };

  const handleVerify = async (employeeIds: number[]) => {
    if (!orgId || employeeIds.length === 0) {
      return;
    }

    setVerifyingIds(prev => new Set([...prev, ...employeeIds]));

    if (!reducedMotion) {
      await new Promise(resolve => setTimeout(resolve, 180));
    }

    try {
      const result = await verifyRosterMembers({ organizationId: orgId, employeeIds }).unwrap();
      const failed = result?.failed || [];
      if (failed.length > 0) {
        addToast(`Verified ${employeeIds.length - failed.length} of ${employeeIds.length} members`, {
          appearance: 'warning',
          autoDismiss: true,
        });
      } else {
        addToast('Members verified', { appearance: 'success', autoDismiss: true });
      }
      setSelectedIds(new Set());
    } catch {
      addToast('Failed to verify members', { appearance: 'error', autoDismiss: true });
    } finally {
      setVerifyingIds(prev => {
        const next = new Set(prev);
        employeeIds.forEach(id => next.delete(id));
        return next;
      });
    }
  };

  const handleArchiveSelected = async (employeeIds: number[]) => {
    if (!orgId || employeeIds.length === 0) {
      return;
    }

    try {
      await archiveRosterMembers({ organizationId: orgId, employeeIds }).unwrap();
      addToast('Members archived', { appearance: 'success', autoDismiss: true });
      setSelectedIds(new Set());
    } catch {
      addToast('Failed to archive selected members', { appearance: 'error', autoDismiss: true });
    }
  };

  const handleUnarchive = async (employeeId: number) => {
    if (!orgId) {
      return;
    }

    try {
      await restoreRosterMembers({ organizationId: orgId, employeeIds: [employeeId] }).unwrap();
      addToast('Member unarchived to Needs Review', { appearance: 'success', autoDismiss: true });
    } catch {
      addToast('Failed to unarchive member', { appearance: 'error', autoDismiss: true });
    }
  };

  const handleRestoreSelected = async (employeeIds: number[]) => {
    if (!orgId || employeeIds.length === 0) {
      return;
    }

    try {
      await unverifyRosterMembers({ organizationId: orgId, employeeIds }).unwrap();
      addToast('Members restored to Needs Review', { appearance: 'success', autoDismiss: true });
      setSelectedIds(new Set());
    } catch {
      addToast('Failed to restore selected members', { appearance: 'error', autoDismiss: true });
    }
  };

  const handleUnarchiveSelected = async (employeeIds: number[]) => {
    if (!orgId || employeeIds.length === 0) {
      return;
    }

    try {
      await restoreRosterMembers({ organizationId: orgId, employeeIds }).unwrap();
      addToast('Members unarchived to Needs Review', { appearance: 'success', autoDismiss: true });
      setSelectedIds(new Set());
    } catch {
      addToast('Failed to unarchive selected members', { appearance: 'error', autoDismiss: true });
    }
  };

  const handleCompleteReview = async () => {
    if (!orgId) {
      return;
    }

    setIsFinalizingReview(true);

    try {
      const unverifiedActiveIds = members
        .filter(member => member.status === ACTIVE_STATUS && !member.verified)
        .map(member => member.id);

      if (unverifiedActiveIds.length > 0) {
        const verifyResult = await verifyRosterMembers({
          organizationId: orgId,
          employeeIds: unverifiedActiveIds,
        }).unwrap();

        const failed = verifyResult?.failed || [];
        if (failed.length > 0) {
          addToast('Unable to finalize because some members could not be verified', {
            appearance: 'error',
            autoDismiss: true,
          });
          return;
        }
      }

      await completeRosterReview({ organizationId: orgId }).unwrap();
      addToast('Roster review finalized', { appearance: 'success', autoDismiss: true });
      setIsFinalizeModalOpen(false);
    } catch {
      addToast('Unable to finalize review', { appearance: 'error', autoDismiss: true });
    } finally {
      setIsFinalizingReview(false);
    }
  };

  const handleCreateMember = async (member: any) => {
    if (!orgId) {
      return;
    }

    setIsCreatingMember(true);
    setCreateError(undefined);

    try {
      await createMember({
        id: 0,
        createEmployee: {
          ...member,
          organization_id: orgId,
        },
      }).unwrap();
      addToast('Member created', { appearance: 'success', autoDismiss: true });
      setIsCreateModalOpen(false);
    } catch {
      setCreateError('Failed to create member');
      addToast('Failed to create member', { appearance: 'error', autoDismiss: true });
    } finally {
      setIsCreatingMember(false);
    }
  };

  const categoryOptions = useMemo(() => {
    const field = demographicFieldEntries.find(item => item.name === listFilterField);
    const validValues = (field?.validValues || []) as Array<{ key: string; text: string }>;
    return validValues.map(option => ({
      key: option.key,
      value: option.text,
      text: option.text,
    }));
  }, [demographicFieldEntries, listFilterField]);

  const activeFilterFieldLabel = useMemo(() => {
    if (!listFilterField) {
      return '';
    }
    return demographicFieldEntries.find(field => field.name === listFilterField)?.displayName || listFilterField;
  }, [demographicFieldEntries, listFilterField]);

  const clearFilter = () => {
    setListFilterField('');
    setListFilterValue('');
  };

  const hasActiveFilter = Boolean(listFilterField && listFilterValue);
  const filterPillText = hasActiveFilter ? `${activeFilterFieldLabel}: ${listFilterValue}` : 'Filter';
  const hideColumn = (column: string) => {
    setVisibleColumns(prev => prev.filter(item => item !== column));
  };

  const rosterTeams = (rosterTeamsData?.teams || []) as Array<{ id: number; name: string; external_id?: string | null }>;

  if (isLoading) {
    return <div>Loading roster review...</div>;
  }

  if (error) {
    return <div>Unable to load roster review.</div>;
  }

  return (
    <InlineDropdownStyle>
      <PageContent style={{ minWidth: 0 }}>
      <PageSection style={{ minWidth: 0 }}>
        <PageHeader
          title="Review Member Roster"
          backText="Back to Member Management"
          onBack={() => navigate('/app/team-management')}
        caption="Review employees, update their information, and confirm the roster before your next survey."
    
          controls={
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <StyledButton
                label={isFinalizingReview || isCompleting ? 'Finalizing...' : 'Finalize Review'}
                color={hasUsersNeedingReview ? 'gray' : 'green'}
                width="130px"
                icon={isFinalizingReview || isCompleting ? <Icon name="spinner" loading /> : null}
                disabled={inFlight || isFinalizingReview}
                onClick={() => setIsFinalizeModalOpen(true)}
              />
            </div>
          }
          divider
        />

        {isRosterFinalized && (
          <FinalizedInfoBanner role="status" aria-live="polite">
            The member roster has been finalized for the upcoming assessment period. Additional edits are allowed
            before the assessment starts; however, changes made afterward may not be included in the results.
          </FinalizedInfoBanner>
        )}

        <Modal
          open={isFinalizeModalOpen}
          size="tiny"
          onClose={() => {
            if (!isFinalizingReview) {
              setIsFinalizeModalOpen(false);
            }
          }}
        >
          <Modal.Header>Finalize Review</Modal.Header>
          <Modal.Content>
            Are you sure you want to finalize the member roster for this assessment period?
          </Modal.Content>
          <Modal.Actions>
            <Button disabled={isFinalizingReview} onClick={() => setIsFinalizeModalOpen(false)}>
              No
            </Button>
            <Button primary loading={isFinalizingReview} disabled={isFinalizingReview} onClick={handleCompleteReview}>
              Yes
            </Button>
          </Modal.Actions>
        </Modal>

        <PageSection display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <TabButton active={activeTab === 'needs-review'} onClick={() => setActiveTab('needs-review')}>
              Needs Review ({counts.needs_review || 0})
            </TabButton>
            <TabButton active={activeTab === 'verified'} onClick={() => setActiveTab('verified')}>
              Verified ({counts.verified || 0})
            </TabButton>
            <TabButton active={activeTab === 'archived'} onClick={() => setActiveTab('archived')}>
              Archived ({counts.archived || 0})
            </TabButton>
          </div>
          <SelectionActionPill
            type="button"
            $tone="green"
            onClick={() => setIsCreateModalOpen(true)}
            aria-label="Add Member"
            title="Add Member"
          >
            <Icon name="add user" />
            Add Member
          </SelectionActionPill>
        </PageSection>

        <PageSection display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Input
              className="roster-search-input"
              placeholder="Search by name or email"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ minWidth: 320 }}
            />
            <Dropdown
              className="filter-pill-dropdown"
              trigger={(
                <FilterTriggerPill $active={hasActiveFilter}>
                  <Icon name="filter" />
                  <span>{filterPillText}</span>
                  {hasActiveFilter && (
                    <button
                      type="button"
                      aria-label="Clear active filter"
                      title="Clear filter"
                      onMouseDown={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        clearFilter();
                      }}
                    >
                      <Icon name="close" />
                    </button>
                  )}
                </FilterTriggerPill>
              )}
              floating
              open={isFilterMenuOpen}
              onOpen={() => setIsFilterMenuOpen(true)}
              onClose={() => setIsFilterMenuOpen(false)}
              closeOnChange={false}
            >
              <Dropdown.Menu style={{ minWidth: 320, padding: 12 }}>
                <div onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#5b6672' }}>Filter</div>
                    <Button
                      basic
                      icon
                      size="mini"
                      aria-label="Close filter menu"
                      title="Close"
                      onClick={() => setIsFilterMenuOpen(false)}
                    >
                      <Icon name="close" />
                    </Button>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#5b6672', marginBottom: 6 }}>Field</div>
                  <Dropdown
                    selection
                    clearable
                    placeholder="Filter field"
                    options={[
                      { key: ALL_FIELDS_OPTION, value: ALL_FIELDS_OPTION, text: 'All Fields' },
                      ...demographicFieldEntries.map(field => ({ key: field.name, value: field.name, text: field.displayName })),
                    ]}
                    value={listFilterField || ALL_FIELDS_OPTION}
                    onChange={(_e, data) => {
                      const nextField = (data.value as string) || '';
                      if (!nextField || nextField === ALL_FIELDS_OPTION) {
                        clearFilter();
                        return;
                      }

                      setListFilterField(nextField);
                      setListFilterValue('');
                    }}
                    fluid
                  />

                  <div style={{ fontSize: 12, fontWeight: 600, color: '#5b6672', margin: '10px 0 6px 0' }}>Value</div>
                  <Dropdown
                    selection
                    clearable
                    search
                    placeholder="Filter value"
                    options={[
                      { key: ALL_VALUES_OPTION, value: ALL_VALUES_OPTION, text: 'All values' },
                      ...categoryOptions,
                    ]}
                    value={listFilterValue || ALL_VALUES_OPTION}
                    onChange={(_e, data) => {
                      const nextValue = (data.value as string) || '';
                      if (!nextValue || nextValue === ALL_VALUES_OPTION) {
                        setListFilterValue('');
                        return;
                      }

                      setListFilterValue(nextValue);
                    }}
                    disabled={!listFilterField}
                    fluid
                  />

                  <Button
                    basic
                    size="mini"
                    style={{ marginTop: 10 }}
                    onClick={clearFilter}
                  >
                    Clear Filter
                  </Button>
                </div>
              </Dropdown.Menu>
            </Dropdown>
            {!isArchivedTab && (
              <Dropdown
                className="columns-pill-dropdown"
                trigger={(
                  <FilterTriggerPill $active={false}>
                    <Icon name="columns" />
                    <span>Columns</span>
                  </FilterTriggerPill>
                )}
                floating
                direction="left"
                closeOnChange={false}
              >
                <Dropdown.Menu style={{ left: 'auto', right: 0, maxHeight: 360, overflowY: 'auto' }}>
                  <Dropdown.Item
                    text="Select All"
                    onClick={() => {
                     const allDemographicColumns = demographicFieldEntries.map(field => field.name);
                      setVisibleColumns(['name', 'email', TEAM_MEMBERSHIP_COLUMN, ...allDemographicColumns]);
                    }}
                  />
                  <Dropdown.Item
                    text="Deselect All"
                    onClick={() => setVisibleColumns(['name'])}
                  />
                  <Dropdown.Divider />
                  <Dropdown.Item>
                    <Checkbox
                      label="Name"
                      checked
                      disabled
                      onClick={e => e.stopPropagation()}
                    />
                  </Dropdown.Item>
                  <Dropdown.Item>
                    <Checkbox
                      label="Email"
                      checked={visibleColumns.includes('email')}
                      onClick={e => e.stopPropagation()}
                      onChange={(_e, data) => {
                        setVisibleColumns(prev => {
                          if (data.checked) {
                            return [...prev, 'email'];
                          }
                          return prev.filter(item => item !== 'email');
                        });
                      }}
                    />
                  </Dropdown.Item>
                  <Dropdown.Item
                    onMouseDown={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <Checkbox
                      label="Teams"
                      checked={visibleColumns.includes(TEAM_MEMBERSHIP_COLUMN)}
                      onClick={e => e.stopPropagation()}
                      onChange={(_e, data) => {
                        setVisibleColumns(prev => {
                          if (data.checked) {
                            return [...prev, TEAM_MEMBERSHIP_COLUMN];
                          }
                          return prev.filter(item => item !== TEAM_MEMBERSHIP_COLUMN);
                        });
                      }}
                    />
                  </Dropdown.Item>
                  {demographicFieldEntries.map(field => (
                    <Dropdown.Item
                      key={field.name}
                      onMouseDown={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      <Checkbox
                        label={field.displayName}
                        checked={visibleColumns.includes(field.name)}
                        onClick={e => e.stopPropagation()}
                        onChange={(_e, data) => {
                          setVisibleColumns(prev => {
                            if (data.checked) {
                              return [...prev, field.name];
                            }
                            return prev.filter(item => item !== field.name);
                          });
                        }}
                      />
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>

          {selectedVisibleIds.length > 0 && (
            <SelectionActionBar>
              <SelectionSummary
                type="button"
                onClick={() => setSelectedIds(new Set())}
                title="Clear selected members"
                aria-label="Clear selected members"
                onMouseEnter={() => setIsSelectionSummaryHovered(true)}
                onMouseLeave={() => setIsSelectionSummaryHovered(false)}
              >
                <Icon name={isSelectionSummaryHovered ? 'close' : 'check'} />
                <span>{selectedVisibleIds.length} members selected</span>
              </SelectionSummary>

              <SelectionPills>
                {activeTab === 'needs-review' && (
                  <>
                    <SelectionActionPill
                      type="button"
                      $tone="green"
                      onClick={() => handleVerify(selectedVisibleIds)}
                      disabled={isVerifying}
                    >
                      <Icon name={isVerifying ? 'spinner' : 'check'} loading={isVerifying} />
                      Verify Selected
                    </SelectionActionPill>
                    <SelectionActionPill
                      type="button"
                      $tone="orange"
                      onClick={() => handleArchiveSelected(selectedVisibleIds)}
                      disabled={isArchiving}
                    >
                      <Icon name={isArchiving ? 'spinner' : 'archive'} loading={isArchiving} />
                      Archive Selected
                    </SelectionActionPill>
                  </>
                )}
                {activeTab === 'verified' && (
                  <SelectionActionPill
                    type="button"
                    $tone="blue"
                    onClick={() => handleRestoreSelected(selectedVisibleIds)}
                    disabled={isUnverifying}
                  >
                    <Icon name={isUnverifying ? 'spinner' : 'undo'} loading={isUnverifying} />
                    Unverify Selected
                  </SelectionActionPill>
                )}
                {activeTab === 'archived' && (
                  <SelectionActionPill
                    type="button"
                    $tone="blue"
                    onClick={() => handleUnarchiveSelected(selectedVisibleIds)}
                    disabled={isRestoring}
                  >
                    <Icon name={isRestoring ? 'spinner' : 'undo alternate'} loading={isRestoring} />
                    Unarchive Selected
                  </SelectionActionPill>
                )}
              </SelectionPills>
            </SelectionActionBar>
          )}

        </PageSection>

        <TableViewport ref={tableViewportRef} className="roster-grid">
          <GridContentTrack style={{ minWidth: `${gridMinWidth}px` }}>
            <GridRow $template={gridTemplateColumns} $header>
              <GridCell $header $align="center" $stickyLeft={0}>
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={selectedVisibleIds.length > 0 && !allVisibleSelected}
                  disabled={filteredMemberIds.length === 0}
                  onChange={() => {
                    if (allVisibleSelected) {
                      setSelectedIds(prev => {
                        const next = new Set(prev);
                        filteredMemberIds.forEach(id => next.delete(id));
                        return next;
                      });
                      return;
                    }

                    setSelectedIds(prev => {
                      const next = new Set(prev);
                      filteredMemberIds.forEach(id => next.add(id));
                      return next;
                    });
                  }}
                />
              </GridCell>
              <GridCell $header $stickyLeft={CHECKBOX_COL_WIDTH} $sortable onClick={() => handleSort('last_name')}>
                <HeaderLabel>
                  <span>Name</span>
                  {sortConfig.key === 'last_name' && (
                    <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                  )}
                </HeaderLabel>
              </GridCell>

              {visibleColumns.includes('email') && (
                <GridCell $header $sortable $fixedWidth={EMAIL_COL_WIDTH} onClick={() => handleSort('email')}>
                  <HeaderCellContent>
                    <HeaderLabel>
                      <span>Email</span>
                      {sortConfig.key === 'email' && (
                        <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                      )}
                    </HeaderLabel>
                    <HeaderHideButton
                      type="button"
                      className="header-hide-button"
                      aria-label="Hide Email column"
                      title="Hide column"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        hideColumn('email');
                      }}
                    >
                      <Icon name="eye slash" />
                    </HeaderHideButton>
                  </HeaderCellContent>
                </GridCell>
              )}

              {dynamicColumns.map(column => {
                let label = column;
                let sortKey = column;

                if (column === TEAM_MEMBERSHIP_COLUMN) {
                  label = 'Teams';
                  sortKey = TEAM_MEMBERSHIP_COLUMN;
                } else if (column === 'departure_date') {
                  label = 'Departure Date';
                } else if (column === 'departure_reason') {
                  label = 'Departure Reason';
                } else {
                  const field = demographicFieldEntries.find(item => item.name === column);
                  if (!field) {
                    return null;
                  }
                  label = field.displayName;
                }

                const canHide = !isArchivedTab;

                return (
                  <GridCell key={`header:${column}`} $header $sortable onClick={() => handleSort(sortKey)}>
                    <HeaderCellContent>
                      <HeaderLabel>
                        <span>{label}</span>
                        {sortConfig.key === sortKey && (
                          <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                        )}
                      </HeaderLabel>
                      {canHide && (
                        <HeaderHideButton
                          type="button"
                          className="header-hide-button"
                          aria-label={`Hide ${label} column`}
                          title="Hide column"
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            hideColumn(column);
                          }}
                        >
                          <Icon name="eye slash" />
                        </HeaderHideButton>
                      )}
                    </HeaderCellContent>
                  </GridCell>
                );
              })}

              {isVerifiedTab && (
                <GridCell $header $sortable onClick={() => handleSort('verified_at')}>
                  <HeaderLabel>
                    <span>Verified At</span>
                    {sortConfig.key === 'verified_at' && (
                      <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                    )}
                  </HeaderLabel>
                </GridCell>
              )}

              <GridCell
                $header
                $align="center"
                $stickyRight
                $compact
                $fixedWidth={ACTION_COL_WIDTH}
                aria-label="Actions"
                title="Actions"
              >
                {(canScrollLeft || canScrollRight) && (
                  <HeaderScrollControls>
                    <HeaderScrollButton
                      type="button"
                      aria-label="Scroll grid left"
                      title="Scroll left"
                      onClick={() => scrollGridHorizontally('left')}
                      disabled={!canScrollLeft}
                    >
                      <Icon name="chevron left" />
                    </HeaderScrollButton>
                    <HeaderScrollButton
                      type="button"
                      aria-label="Scroll grid right"
                      title="Scroll right"
                      onClick={() => scrollGridHorizontally('right')}
                      disabled={!canScrollRight}
                    >
                      <Icon name="chevron right" />
                    </HeaderScrollButton>
                  </HeaderScrollControls>
                )}
              </GridCell>
            </GridRow>

            {sortedMembers.length === 0 && (
              <GridRow $template={gridTemplateColumns}>
                <GridCell $stickyLeft={0} $align="center">-</GridCell>
                <GridCell $stickyLeft={CHECKBOX_COL_WIDTH}>No members in this view.</GridCell>
                {visibleColumns.includes('email') && <GridCell />}
                {dynamicColumns.map(column => <GridCell key={`empty:${column}`} />)}
                {isVerifiedTab && <GridCell />}
                <GridCell $stickyRight $compact $fixedWidth={ACTION_COL_WIDTH} />
              </GridRow>
            )}

            {sortedMembers.map(member => {
              const rowAnimating = verifyingIds.has(member.id);
              const memberDisplayName = `${member.last_name || ''}, ${member.first_name || ''}`;
              const nameCellAction: (NameCellIndicator & { onClick: () => void }) | null =
                activeTab === 'needs-review' && member.status === ACTIVE_STATUS && !member.verified
                ? {
                    label: `Verify ${memberDisplayName}`,
                    icon: 'check circle',
                    color: '#16a34a',
                    onClick: () => handleVerify([member.id]),
                  }
                : activeTab === 'verified' && member.status === ACTIVE_STATUS && member.verified
                  ? {
                      label: `Unverify ${memberDisplayName}`,
                      icon: 'circle outline',
                      color: '#16a34a',
                      onClick: () => handleRestoreSelected([member.id]),
                    }
                  : activeTab === 'archived' && member.status !== ACTIVE_STATUS
                    ? {
                        label: `Unarchive ${memberDisplayName}`,
                        icon: 'undo alternate',
                        color: '#1e40af',
                        onClick: () => handleUnarchiveSelected([member.id]),
                      }
                    : null;
              const isHoveringNameAction = Boolean(nameCellAction && hoveredActionMemberId === member.id);
              const statusIndicator: NameCellIndicator = member.status !== ACTIVE_STATUS
                ? { icon: 'archive', label: 'Archived', color: '#6b7280' }
                : member.verified
                  ? { icon: 'check circle', label: 'Verified', color: '#16a34a' }
                  : { icon: 'circle outline', label: 'Needs Review', color: '#16a34a' };
              const displayedIndicator: NameCellIndicator = isHoveringNameAction && nameCellAction
                ? {
                    icon: nameCellAction.icon,
                    label: nameCellAction.label,
                    color: nameCellAction.color,
                  }
                : statusIndicator;
              const rowStyle = rowAnimating
                ? {
                    backgroundColor: '#e8f7eb',
                    opacity: reducedMotion ? 1 : 0.25,
                    transform: reducedMotion ? 'none' : 'translateY(-2px)',
                    transition: reducedMotion ? 'none' : 'all 180ms ease',
                  }
                : undefined;

              return (
                <GridRow
                  key={member.id}
                  $template={gridTemplateColumns}
                  $editing={editingCell?.memberId === member.id}
                  style={rowStyle as React.CSSProperties}
                >
                  <GridCell $align="center" $stickyLeft={0}>
                    <Checkbox checked={selectedIds.has(member.id)} onChange={() => toggleSelection(member.id)} />
                  </GridCell>

                  <GridCell $stickyLeft={CHECKBOX_COL_WIDTH}>
                    <EditableCell title={memberDisplayName} className="truncate">
                      <MemberNameContent
                        role={nameCellAction ? 'button' : undefined}
                        tabIndex={nameCellAction ? 0 : undefined}
                        aria-label={nameCellAction?.label}
                        title={nameCellAction?.label}
                        $clickable={Boolean(nameCellAction)}
                        onMouseEnter={
                          nameCellAction ? () => setHoveredActionMemberId(member.id) : undefined
                        }
                        onMouseLeave={
                          nameCellAction ? () => setHoveredActionMemberId(prev => (prev === member.id ? null : prev)) : undefined
                        }
                        onClick={
                          nameCellAction ? nameCellAction.onClick : undefined
                        }
                        onKeyDown={
                          nameCellAction
                            ? (e: React.KeyboardEvent<HTMLSpanElement>) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  nameCellAction.onClick();
                                }
                              }
                            : undefined
                        }
                      >
                        <MemberStatusIndicator
                          aria-label={`${displayedIndicator.label} status`}
                          title={displayedIndicator.label}
                        >
                          <Icon name={displayedIndicator.icon} style={{ color: displayedIndicator.color }} />
                        </MemberStatusIndicator>
                        <span>{member.last_name}, {member.first_name}</span>
                      </MemberNameContent>
                    </EditableCell>
                  </GridCell>

                  {visibleColumns.includes('email') && (
                    <GridCell $fixedWidth={EMAIL_COL_WIDTH}>
                      {editingCell?.memberId === member.id && editingCell?.field === 'email' ? (
                        <Input
                          autoFocus
                          fluid
                          value={editingCell.value}
                          onChange={e => setEditingCell({ ...editingCell, value: e.target.value })}
                          onKeyDown={async (e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === 'Enter') {
                              await saveCell(member, 'email', editingCell.value);
                              setEditingCell(null);
                            }
                            if (e.key === 'Escape') {
                              setEditingCell(null);
                            }
                          }}
                          onBlur={async () => {
                            await saveCell(member, 'email', editingCell.value);
                            setEditingCell(null);
                          }}
                        />
                      ) : (
                        <EditableCell
                          title={member.email || ''}
                          className={`truncate ${recentlySavedCells.has(`${member.id}:email`) ? 'recently-saved' : ''}`}
                          onClick={() => setEditingCell({ memberId: member.id, field: 'email', value: member.email || '' })}
                        >
                          {member.email}
                        </EditableCell>
                      )}
                    </GridCell>
                  )}

                  {dynamicColumns.map(column => {
                    if (column === TEAM_MEMBERSHIP_COLUMN) {
                      const teamMemberships = member.team_memberships || [];
                      const selectedTeamIds = teamMemberships.map(team => team.team_id);
                      const value = teamMemberships
                        .map(team => team.team_external_id)
                        .filter((externalId): externalId is string => Boolean(externalId))
                        .join(', ');
                      const cellId = `${member.id}:${TEAM_MEMBERSHIP_COLUMN}`;
                      const isEditing = editingCell?.memberId === member.id && editingCell.field === TEAM_MEMBERSHIP_COLUMN;

                      return (
                        <GridCell key={cellId} $allowOverflow={isEditing}>
                          {isEditing ? (
                            <Dropdown
                              className="roster-inline-dropdown"
                              fluid
                              selection
                              multiple
                              search
                              closeOnChange
                              options={rosterTeams.map(team => ({
                                key: team.id,
                                value: team.id,
                                text: `${team.name}${team.external_id ? ` (${team.external_id})` : ' (No external id)'}`,
                              }))}
                              value={editingCell.value || []}
                              onChange={async (_e, data) => {
                                const selected = ((data.value as Array<number | string>) || []).map(value => Number(value));
                                setEditingCell({ ...editingCell, value: selected });
                                await saveCell(member, TEAM_MEMBERSHIP_COLUMN, selected);
                              }}
                              onClose={() => {
                                setEditingCell(null);
                              }}
                            />
                          ) : (
                            <EditableCell
                              title={value || '-'}
                              className={recentlySavedCells.has(cellId) ? 'recently-saved' : ''}
                              onClick={() => setEditingCell({
                                memberId: member.id,
                                field: TEAM_MEMBERSHIP_COLUMN,
                                value: selectedTeamIds,
                              })}
                            >
                              {value || '-'}
                            </EditableCell>
                          )}
                        </GridCell>
                      );
                    }

                    if (column === 'departure_date') {
                      const value = member.departure_date || '';
                      const cellId = `${member.id}:departure_date`;
                      const isEditing = editingCell?.memberId === member.id && editingCell.field === 'departure_date';

                      return (
                        <GridCell key={cellId} $allowOverflow={isEditing}>
                          {isEditing ? (
                            <Input
                              autoFocus
                              fluid
                              type="date"
                              value={editingCell.value}
                              onChange={e => setEditingCell({ ...editingCell, value: e.target.value })}
                              onKeyDown={async (e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (e.key === 'Enter') {
                                  await saveCell(member, 'departure_date', editingCell.value);
                                  setEditingCell(null);
                                }
                                if (e.key === 'Escape') {
                                  setEditingCell(null);
                                }
                              }}
                              onBlur={async () => {
                                await saveCell(member, 'departure_date', editingCell.value);
                                setEditingCell(null);
                              }}
                            />
                          ) : (
                            <EditableCell
                              title={value || '-'}
                              className={recentlySavedCells.has(cellId) ? 'recently-saved' : ''}
                              onClick={() => setEditingCell({ memberId: member.id, field: 'departure_date', value })}
                            >
                              {value || '-'}
                            </EditableCell>
                          )}
                        </GridCell>
                      );
                    }

                    if (column === 'departure_reason') {
                      const value = member.departure_reason || '';
                      const displayValue = END_OF_SERVICE_REASONS.find(reason => reason.value === value)?.label || value || '-';
                      const cellId = `${member.id}:departure_reason`;
                      const isEditing = editingCell?.memberId === member.id && editingCell.field === 'departure_reason';

                      return (
                        <GridCell key={cellId} $allowOverflow={isEditing}>
                          {isEditing ? (
                            <Dropdown
                              className="roster-inline-dropdown"
                              fluid
                              selection
                              clearable
                              open
                              options={END_OF_SERVICE_REASONS.map(reason => ({
                                key: reason.value,
                                text: reason.label,
                                value: reason.value,
                              }))}
                              value={editingCell.value || undefined}
                              onChange={async (_e, data) => {
                                const selected = (data.value as string) || '';
                                setEditingCell({ ...editingCell, value: selected });
                                await saveCell(member, 'departure_reason', selected);
                                setEditingCell(null);
                              }}
                              onBlur={() => setEditingCell(null)}
                            />
                          ) : (
                            <EditableCell
                              title={displayValue}
                              className={recentlySavedCells.has(cellId) ? 'recently-saved' : ''}
                              onClick={() => setEditingCell({ memberId: member.id, field: 'departure_reason', value })}
                            >
                              {displayValue}
                            </EditableCell>
                          )}
                        </GridCell>
                      );
                    }

                    const field = demographicFieldEntries.find(item => item.name === column);
                    if (!field) {
                      return null;
                    }

                    const value = member.demographic_values?.[field.name] || '';
                    const cellId = `${member.id}:${field.name}`;
                    const isEditing = editingCell?.memberId === member.id && editingCell.field === field.name;
                    const validValues = (field.validValues || []) as Array<{ key: string; text: string }>;

                    return (
                      <GridCell key={cellId} $allowOverflow={isEditing}>
                        {isEditing ? (
                          <Dropdown
                            className="roster-inline-dropdown"
                            fluid
                            selection
                            search
                            open
                            options={validValues.map(option => ({
                              key: option.key,
                              value: option.text,
                              text: option.text,
                            }))}
                            value={editingCell.value}
                            onChange={async (_e, data) => {
                              const selected = (data.value as string) || '';
                              setEditingCell({ ...editingCell, value: selected });
                              await saveCell(member, field.name, selected);
                              setEditingCell(null);
                            }}
                            onBlur={() => setEditingCell(null)}
                          />
                        ) : (
                          <EditableCell
                            title={value || '-'}
                            className={recentlySavedCells.has(cellId) ? 'recently-saved' : ''}
                            onClick={() => setEditingCell({ memberId: member.id, field: field.name, value })}
                          >
                            {value || '-'}
                          </EditableCell>
                        )}
                      </GridCell>
                    );
                  })}

                  {isVerifiedTab && (
                    <GridCell title={`${member.verified_at ? new Date(member.verified_at).toLocaleDateString() : '-'}${member.verified_by ? ` by ${member.verified_by}` : ''}`}>
                      <EditableCell as="span" title={`${member.verified_at ? new Date(member.verified_at).toLocaleDateString() : '-'}${member.verified_by ? ` by ${member.verified_by}` : ''}`}>
                        {member.verified_at ? new Date(member.verified_at).toLocaleDateString() : '-'}
                        {member.verified_by ? ` by ${member.verified_by}` : ''}
                      </EditableCell>
                    </GridCell>
                  )}

                  <GridCell $align="center" $stickyRight $compact $fixedWidth={ACTION_COL_WIDTH}>
                    {activeTab === 'needs-review' && (
                      <RowActionIcons>
                        <IconActionButton
                          type="button"
                          aria-label="Verify member"
                          title="Verify"
                          onClick={() => handleVerify([member.id])}
                          disabled={isVerifying && verifyingIds.has(member.id)}
                        >
                          <Icon name="check circle outline" color="green" />
                        </IconActionButton>
                        <IconActionButton
                          type="button"
                          aria-label="Archive member"
                          title="Archive"
                          onClick={() => handleArchiveSelected([member.id])}
                          disabled={isArchiving}
                        >
                          <Icon name="archive" style={{ color: '#8d949e' }} />
                        </IconActionButton>
                      </RowActionIcons>
                    )}
                    {activeTab === 'verified' && (
                      <RowActionIcons>
                        <IconActionButton
                          type="button"
                          aria-label="Restore member to review"
                          title="Restore to Review"
                          onClick={() => unverifyRosterMembers({ organizationId: orgId || 0, employeeIds: [member.id] })}
                          disabled={isUnverifying}
                        >
                          <Icon name="undo" style={{ color: '#4a6fa5', fontSize: '0.95rem' }} loading={isUnverifying} />
                        </IconActionButton>
                      </RowActionIcons>
                    )}
                    {activeTab === 'archived' && (
                      <RowActionIcons>
                        <IconActionButton
                          type="button"
                          aria-label="Unarchive member"
                          title="Unarchive"
                          onClick={() => handleUnarchive(member.id)}
                          disabled={isRestoring}
                        >
                          <Icon name="undo alternate" style={{ color: '#6b7280', fontSize: '0.95rem' }} loading={isRestoring} />
                        </IconActionButton>
                      </RowActionIcons>
                    )}
                  </GridCell>
                </GridRow>
              );
            })}
          </GridContentTrack>
        </TableViewport>

        <MemberCreateModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSave={handleCreateMember}
          loading={isCreatingMember}
          error={createError}
        />
      </PageSection>
      </PageContent>
    </InlineDropdownStyle>
  );
};

export default RosterReviewPage;

const TabButton = styled.button<{ active: boolean }>`
  border: none;
  border-bottom: 2px solid ${props => (props.active ? '#1f70c1' : 'transparent')};
  color: ${props => (props.active ? '#1f2937' : '#5b6470')};
  background: transparent;
  border-radius: 0;
  padding: 8px 10px 9px 10px;
  font-weight: ${props => (props.active ? 700 : 500)};
  font-size: 15px;
  cursor: pointer;

  &:hover {
    color: #1f2937;
  }
`;

const EditableCell = styled.div`
  min-height: 26px;
  max-width: 100%;
  width: 100%;
  border-radius: 4px;
  padding: 4px 6px;
  cursor: pointer;
  transition: background-color 120ms ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.truncate {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    background-color: #f5f9ff;
  }

  &.recently-saved {
    background-color: #e8f7eb;
  }
`;

const MemberNameContent = styled.span<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  width: 100%;
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};

  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:focus-visible {
    outline: ${props => (props.$clickable ? '2px solid #93c5fd' : 'none')};
    outline-offset: 1px;
    border-radius: 4px;
  }
`;

const MemberStatusIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  min-width: 16px;

  i.icon {
    margin: 0;
    font-size: 14px;
    line-height: 1;
  }
`;

const FinalizedInfoBanner = styled.div`
  margin: 0 0 12px 0;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #9dc6f0;
  background: #eaf4ff;
  color: #154273;
  font-size: 14px;
  line-height: 1.5;
`;

const SelectionPills = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const SelectionActionBar = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 0;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  width: fit-content;
  max-width: 100%;
`;

const SelectionSummary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  color: #111827;
  font-size: 13px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;

  > i.icon {
    margin: 0;
    font-size: 17px;
    color: #1f2937;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    line-height: 1;
    vertical-align: middle;
  }

  > span {
    font-size: 13px;
    font-weight: 500;
  }

  &:hover > span {
    color: #0f172a;
  }
`;

const SelectionActionPill = styled.button<{ $tone: 'green' | 'orange' | 'blue' }>`
  border: 1px solid
    ${props => {
      if (props.$tone === 'green') return 'rgba(46, 125, 50, 0.35)';
      if (props.$tone === 'orange') return 'rgba(217, 119, 6, 0.35)';
      return 'rgba(37, 99, 235, 0.35)';
    }};
  background: ${props => {
    if (props.$tone === 'green') return 'rgba(232, 245, 233, 0.9)';
    if (props.$tone === 'orange') return 'rgba(255, 247, 237, 0.95)';
    return 'rgba(239, 246, 255, 0.95)';
  }};
  color: ${props => {
    if (props.$tone === 'green') return '#1f6a2b';
    if (props.$tone === 'orange') return '#9a4d03';
    return '#1e40af';
  }};
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;

  i.icon {
    margin: 0;
    font-size: 13px;
    line-height: 1;
  }

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$tone === 'green') return 'rgba(220, 238, 222, 1)';
      if (props.$tone === 'orange') return 'rgba(255, 237, 213, 1)';
      return 'rgba(219, 234, 254, 1)';
    }};
  }

  &:disabled {
    border-color: #d1d5db;
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }

  &:disabled i.icon {
    color: #9ca3af;
  }
`;

const FilterTriggerPill = styled.div<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  max-width: 380px;
  gap: 6px;
  border: 1px solid ${props => (props.$active ? '#cfe0f5' : '#d4d4d5')};
  background: ${props => (props.$active ? '#f5f9ff' : '#f3f4f6')};
  color: ${props => (props.$active ? '#36557a' : '#4b5563')};
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;

  > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > i.icon {
    margin: 0;
    font-size: 12px;
    line-height: 1;
  }

  button {
    border: none;
    background: transparent;
    color: ${props => (props.$active ? '#4a6fa5' : '#6b7280')};
    width: 18px;
    height: 18px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
  }

  button:hover {
    background: ${props => (props.$active ? 'rgba(74, 111, 165, 0.14)' : 'rgba(107, 114, 128, 0.14)')};
  }

  button i.icon {
    margin: 0;
    font-size: 10px;
    line-height: 1;
  }
`;

const RowActionIcons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
`;

const IconActionButton = styled.button`
  border: none;
  background: transparent;
  border-radius: 6px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover:not(:disabled) {
    background: #eef2f7;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  i.icon {
    margin: 0;
    font-size: 1.05rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    width: 1em;
    height: 1em;
    vertical-align: middle;
  }
`;

const TableViewport = styled.div`
  position: relative;
  isolation: isolate;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  max-height: min(64vh, 680px);
  overflow-x: auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable both-edges;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c7ccd4;
    border-radius: 999px;
  }

  &::-webkit-scrollbar-track {
    background: #f3f4f6;
  }
`;

const GridContentTrack = styled.div`
  width: 100%;
  min-width: 100%;
`;

const GridRow = styled.div<{ $template: string; $header?: boolean; $editing?: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.$template};
  min-width: 100%;
  align-items: stretch;
  border-bottom: 1px solid #edf1f5;
  background: ${props => (props.$header ? '#f8fafc' : '#ffffff')};
  position: ${props => (props.$header ? 'sticky' : 'relative')};
  top: ${props => (props.$header ? '0' : 'auto')};
  z-index: ${props => {
    if (props.$header) return 14;
    if (props.$editing) return 8;
    return 1;
  }};

  &:hover {
    background: ${props => (props.$header ? '#f8fafc' : '#fbfdff')};
  }
`;

const GridCell = styled.div<{
  $header?: boolean;
  $align?: 'left' | 'center' | 'right';
  $stickyLeft?: number;
  $stickyRight?: boolean;
  $sortable?: boolean;
  $compact?: boolean;
  $fixedWidth?: number;
  $allowOverflow?: boolean;
}>`
  min-width: 0;
  white-space: nowrap;
  overflow: ${props => (props.$allowOverflow ? 'visible' : 'hidden')};
  text-overflow: ${props => (props.$allowOverflow ? 'clip' : 'ellipsis')};
  position: relative;
  z-index: ${props => (props.$allowOverflow ? 12 : 'auto')};
  padding: ${props => {
    if (props.$compact) {
      return props.$header ? '8px 2px' : '6px 2px';
    }
    return props.$header ? '10px 10px' : '8px 8px';
  }};
  font-size: ${props => (props.$header ? '13px' : '12px')};
  font-weight: ${props => (props.$header ? 600 : 400)};
  color: ${props => (props.$header ? '#334155' : '#1f2937')};
  text-align: ${props => props.$align || 'left'};
  display: flex;
  align-items: center;
  justify-content: ${props => {
    if (props.$align === 'center') return 'center';
    if (props.$align === 'right') return 'flex-end';
    return 'flex-start';
  }};
  border-right: 1px solid #f1f5f9;
  cursor: ${props => (props.$sortable ? 'pointer' : 'default')};
  ${props => props.$fixedWidth && `
    width: ${props.$fixedWidth}px;
    min-width: ${props.$fixedWidth}px;
    max-width: ${props.$fixedWidth}px;
  `}

  ${props => props.$header && 'background: #f8fafc; z-index: 9;'}

  ${props => typeof props.$stickyLeft === 'number' && `
    position: sticky;
    left: ${props.$stickyLeft}px;
    top: ${props.$header ? '0' : 'auto'};
    z-index: ${props.$header ? 10 : 6};
    background: ${props.$header ? '#f8fafc' : '#ffffff'};
    box-shadow: 1px 0 0 #e5e7eb;
  `}

  ${props => props.$stickyRight && `
    position: sticky;
    right: 0;
    top: ${props.$header ? '0' : 'auto'};
    z-index: ${props.$header ? 10 : 6};
    background: ${props.$header ? '#f8fafc' : '#ffffff'};
    box-shadow: -1px 0 0 #e5e7eb;
    border-right: none;
  `}
`;

const HeaderLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > i.icon {
    margin: 0 !important;
    font-size: 12px;
    color: #5b6470;
  }
`;

const HeaderCellContent = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;

  .header-hide-button {
    opacity: 0;
    pointer-events: none;
  }

  &:hover .header-hide-button,
  &:focus-within .header-hide-button {
    opacity: 1;
    pointer-events: auto;
  }
`;

const HeaderHideButton = styled.button`
  margin-left: auto;
  border: none;
  background: transparent;
  color: #8a94a3;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background-color 120ms ease, color 120ms ease, opacity 120ms ease;

  &:hover {
    background: #eef2f7;
    color: #5f6b7a;
  }

  i.icon {
    margin: 0;
    font-size: 11px;
    line-height: 1;
  }
`;

const HeaderScrollControls = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
`;

const HeaderScrollButton = styled.button`
  border: 1px solid #c7d2e2;
  background: #eef4ff;
  border-radius: 6px;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: background-color 120ms ease, border-color 120ms ease, transform 120ms ease;

  &:hover:not(:disabled) {
    background: #dce9ff;
    border-color: #9bb9ea;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
    transform: none;
  }

  i.icon {
    margin: 0;
    font-size: 0.9rem;
    color: #375e96;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    width: 1em;
    height: 1em;
  }
`;

const InlineDropdownStyle = styled.div`
  .roster-search-input.ui.input > input {
    height: 32px;
    min-height: 32px;
    padding-top: 6px;
    padding-bottom: 6px;
    font-size: 13px;
  }

  .filter-pill-dropdown.ui.dropdown > .dropdown.icon {
    display: none !important;
  }

  .columns-pill-dropdown.ui.dropdown > .dropdown.icon {
    display: none !important;
  }

  .roster-grid .roster-inline-dropdown.ui.selection.dropdown {
    min-width: 0 !important;
    width: 100% !important;
    max-width: 100%;
    z-index: 3;
    font-size: 12px;
    line-height: 1.25;
  }

  .roster-grid .roster-inline-dropdown.ui.selection.dropdown > .text,
  .roster-grid .roster-inline-dropdown.ui.selection.dropdown > .default.text,
  .roster-grid .roster-inline-dropdown.ui.selection.dropdown > .dropdown.icon {
    font-size: 12px;
  }

  .roster-grid .roster-inline-dropdown.ui.selection.dropdown .menu {
    min-width: max(100%, 220px) !important;
    max-width: 360px;
    max-height: min(240px, 40vh);
    overflow-y: auto;
    right: 0;
    left: auto;
    z-index: 120;
    border-top: 1px solid rgba(34, 36, 38, 0.15) !important;
  }

  .roster-grid .roster-inline-dropdown.ui.selection.dropdown .menu > .item {
    font-size: 12px;
    line-height: 1.3;
    white-space: normal;
  }

  .roster-grid .ui.input,
  .roster-grid .ui.input > input {
    width: 100%;
    min-width: 0;
  }
`;
